import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Download, PanelRightClose, PanelRightOpen } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import {
  downloadBase64Pdf,
  suggestChatReplyPdfFileName,
  titleCasePdfName,
} from "../../../lib/chatPdfApi";

const LABELED_MIN_WIDTH = 360;

/**
 * Inline chip when the supervisor returned a PDF attachment (user asked for export).
 * Opens the side preview panel or downloads the server-generated PDF.
 */
export default function ChatPdfDownloadCard({
  attachment,
  pdfFileName,
  messageText,
  onOpenPanel,
  highlightOpen,
  className,
}) {
  const rootRef = useRef(null);
  const [showLabels, setShowLabels] = useState(true);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const update = (width) => setShowLabels(width >= LABELED_MIN_WIDTH);
    update(el.getBoundingClientRect().width);

    const ro = new ResizeObserver(([entry]) => {
      update(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const displayFileName =
    attachment?.filename || suggestChatReplyPdfFileName(messageText, pdfFileName);

  const fileName = titleCasePdfName(displayFileName);
  const [downloading, setDownloading] = useState(false);
  const PreviewIcon = highlightOpen ? PanelRightClose : PanelRightOpen;

  const handleDownload = useCallback(async () => {
    if (!attachment?.data || downloading) return;
    setDownloading(true);
    try {
      downloadBase64Pdf(attachment.data, fileName);
    } finally {
      setDownloading(false);
    }
  }, [attachment?.data, fileName, downloading]);

  if (!attachment?.data) return null;

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full rounded-xl border px-3 py-2.5",
        "border-violet-500/35 bg-white text-foreground shadow-sm",
        "dark:border-violet-500/40 dark:bg-[hsl(240,4%,10%)] dark:shadow-none",
        highlightOpen && "ring-2 ring-violet-500/35 ring-offset-2 ring-offset-background dark:ring-offset-[hsl(240,4%,10%)]",
        className
      )}
      role="group"
      aria-label={`PDF attachment: ${fileName}`}
    >
      <div
        className={cn(
          "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-2",
          showLabels && "grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3"
        )}
      >
        <div
          className={cn(
            "col-start-1 row-start-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border",
            "border-violet-500/30 text-violet-600 bg-violet-500/[0.06]",
            "dark:border-violet-400/35 dark:text-violet-300 dark:bg-violet-500/10"
          )}
        >
          <FileText className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        </div>
        <div className="col-start-2 row-start-1 min-w-0 overflow-hidden">
          <p className="truncate text-sm font-semibold text-foreground">{fileName}</p>
          <p className="truncate text-xs text-muted-foreground">PDF ready</p>
        </div>
        <div
          className={cn(
            "col-span-2 row-start-2 flex shrink-0 items-center justify-end gap-2",
            showLabels && "col-span-1 col-start-3 row-start-1"
          )}
        >
          <Button
            type="button"
            variant="outline"
            className={cn(
              "h-8 shrink-0 px-0",
              showLabels ? "w-auto px-3" : "w-8",
              "border-violet-600/40 text-violet-700 hover:bg-violet-500/10 dark:border-violet-400/50 dark:text-violet-300"
            )}
            onClick={onOpenPanel}
            aria-label={highlightOpen ? "Close preview" : "Open preview"}
          >
            <PreviewIcon className={cn("h-3.5 w-3.5", showLabels && "mr-1.5")} aria-hidden />
            {showLabels ? <span>{highlightOpen ? "Close" : "Preview"}</span> : null}
          </Button>
          <Button
            type="button"
            className={cn(
              "relative h-8 shrink-0 px-0",
              showLabels ? "w-auto px-3" : "w-8",
              "bg-violet-600 hover:bg-violet-700"
            )}
            disabled={downloading}
            aria-busy={downloading}
            aria-label="Download"
            onClick={handleDownload}
          >
            <span className={cn("inline-flex items-center justify-center", downloading && "invisible")}>
              <Download className={cn("h-3.5 w-3.5", showLabels && "mr-1.5")} aria-hidden />
              {showLabels ? <span>Download</span> : null}
            </span>
            {downloading ? (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <LoadingSpinner />
              </span>
            ) : null}
          </Button>
        </div>
      </div>
    </div>
  );
}
